/**
 * Canonical shape of an internal link target — ONE definition (rule 64).
 *
 * The content Worker rewrites internal hrefs at serve time
 * (`canonicalizeInternalHrefs`, edge-db/shared/served-html-hygiene.ts), and the
 * app applies the same rule (src/lib/crawl-policy.ts). If a checker read
 * the RAW href while the serving layer rewrites it, a link that resolves
 * perfectly in production would be reported as absent (2026-07-26 incident,
 * when the static-SEO exporter's link-population gate aborted a publish; that
 * exporter was deleted in C09, 2026-10). Never hand-copy this rule into a
 * consumer — that is the drifting-copies class (rules 23/24).
 *
 * Bare-Node callers import this module, so the one shared helper import
 * carries an explicit extension (rule 64). Taking the legacy map as an argument
 * keeps every other canonical rewrite dependency-free.
 */

import { withTrailingSlash } from "../../shared/canonical-url.ts";
import crawlPolicyData from "../../src/data/crawl-policy-data.json" with { type: "json" };
import { goneFamilyHubPath } from "./gone-paths.ts";

/**
 * Nakshatra ids are underscore-separated internally (`purva_ashadha`) while the
 * published route is hyphenated (`/nakshatra/purva-ashadha/`). Only the six
 * two-word nakshatras carry an underscore, so single-word ids are untouched.
 */
const NAKSHATRA_UNDERSCORE_ID_RE = /^\/nakshatra\/([^/?#]*_[^/?#]*)(\/.*)?$/;

const LEGACY_PATTERNS = crawlPolicyData.legacyCrawlCanonicalPathPatterns.map((entry) => ({
  pattern: new RegExp(entry.pattern),
  target: entry.target,
}));

/** Exact redirects take precedence over governed pattern-shaped retirements. */
export function legacyCanonicalPath(
  path: string,
  legacyCanonicalPaths: Readonly<Record<string, string>>
): string | null {
  const normalized = withTrailingSlash(path);
  const exact = legacyCanonicalPaths[path] ?? legacyCanonicalPaths[normalized];
  if (exact) return exact;
  for (const { pattern, target } of LEGACY_PATTERNS) {
    const match = pattern.exec(normalized);
    if (match) {
      const resolved = target.replace(/\$(\d)/g, (_, index) => match[Number(index)] ?? "");
      // Exact-map targets are already checked to be terminal destinations.
      // A retired year must reach that destination without a second redirect.
      return legacyCanonicalPaths[resolved] ?? resolved;
    }
  }
  return null;
}

/**
 * Return the canonical path a crawler/user is actually served for `path`.
 * Query/fragment must be split off by the caller; this operates on the path
 * alone and returns `path` unchanged when no rewrite applies.
 */
export function canonicalizeInternalPath(
  path: string,
  legacyCanonicalPaths: Readonly<Record<string, string>>
): string {
  if (!path.startsWith("/")) return path;

  const legacy = legacyCanonicalPath(path, legacyCanonicalPaths);
  if (legacy) return legacy;

  // A removed templated family answers 410 (2026-09-23). A stored body that
  // still links one is sent to the family hub, which computes the same answer
  // for any city, pair or sign, instead of to a dead URL.
  const goneHub = goneFamilyHubPath(path);
  if (goneHub) return goneHub;

  const nakshatra = NAKSHATRA_UNDERSCORE_ID_RE.exec(path);
  if (nakshatra) {
    return `/nakshatra/${nakshatra[1].replace(/_/g, "-")}${nakshatra[2] ?? "/"}`;
  }

  return path;
}
